import styled, { css } from "styled-components";
import { forwardRef, useState, useEffect } from "react";
import {
  Grid,
  Stack,
  Autocomplete,
  createFilterOptions,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import TextField from "src/components/core/TextField";
import font from "src/styles/font";

import { useDialog } from "src/context/DialogContext";
import { UseUpload } from "src/context/UploadProductContext";

import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import { getLocale } from "react-i18nify";
import { formatFromElastic } from "src/common";

import { searchProduct } from "src/api/product";
import { convertPrice } from "src/common";

import SpottableImage from "src/components/HotspotImage/SpottableImage";

const S = {};

S.PreviewImageUploadBox = styled.div`
  position: relative;
  padding-top: 100%;
  background-color: #f3f1f2;
  border-radius: 24px;
  /* grid-column: span 7; */
`;

S.UploadInnerBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.UploadInput = styled.label`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

S.UploadLabel = styled.span`
  font-size: ${font.size.md};
  font-weight: bold;
  color: #6c6c70;
  margin-top: 16px;

  &::after {
    content: "*";
    font-weight: 600;
    color: #e04132;
  }
`;

S.UploadHelperText = styled.span`
  display: flex;
  justify-content: center;
  font-size: ${font.size.sm};
  color: #6c6c70;
  margin-top: 8px;
`;

S.MediaUploaded = styled.div`
  position: relative;
`;

const ImageEmpty = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: #f2f1f3;
  border-radius: 16px;
  object-fit: cover;
  object-position: center;
  cursor: pointer;
  vertical-align: initial;
  margin-right: 8px;
  &:hover {
    opacity: 0.8;
  }

  &.active {
    border: 2px solid #707070;
  }
`;

const ProductChoiceDetail = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 35% 35% 30%;
  align-items: center;
  padding: 5px 0;
`;

const SpanProductDetail = styled.span`
  color: ${({ nameProd }) => (nameProd ? "#252525" : "#6C6C70")};
  font-size: 1rem;
  font-weight: 400;
  text-align: ${({ center }) => (center ? "center" : "left")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ price }) =>
    price &&
    css`
      color: #e13b30;
      font-size: 1.25rem;
      font-weight: 600;

      align-self: flex-end;
      text-align: right;
      grid-column: 3;
      grid-row: 1 / span 2;
    `}
`;

const Step2 = ({ onChange, formData, formControl, formErrors }) => {
  const { openDialog, closeDialog } = useDialog();
  const { masterdata } = UseUpload();
  const sortHashtag = masterdata.hashtag_data.sort((a, b) => {
    return b?.mood_count ?? 0 - a?.mood_count ?? 0;
  });

  const [productList, setProductList] = useState([]);

  useEffect(() => {
    (async () => {
      await searchProduct({}).then(async ({ data }) => {
        setProductList(
          data?.rows.length > 0
            ? data?.rows?.map((product) => ({
                ...formatFromElastic(product),
              }))
            : []
        );
      });
    })();
  }, []);

  return (
    <Grid container>
      <Grid item md={5}>
        <S.MediaUploaded>
          <SpottableImage
            formControl={formControl}
            aspectRatio={1}
            previewImage={
              typeof formData?.media?.image === "string"
                ? formData?.media?.image
                : URL.createObjectURL(formData?.media?.image)
            }
            hotspot={formData?.media?.hotspot}
            hotspotArrayKey="media.hotspot"
            mode={"edit"}
            productOptions={formData?.product_choice_moods}
            onSelectProduct={(value, spotIndex) => {
              onChange(null, `media.hotspot.${spotIndex}`, value);
            }}
            Components={{
              Hotspot: ({ index, ...props }) => {},
              HotspotHover: ({ index, ...props }) => {},
            }}
          />
        </S.MediaUploaded>
        {formErrors[`media.hotspot`] ? (
          <FormHelperText error={true}>
            กรุณาระบุสินค้าที่มีในภาพอย่างน้อย 1 รายการ
          </FormHelperText>
        ) : !Object.keys(formErrors).every(
            (key) => !key.includes("media.hotspot")
          ) ? (
          <FormHelperText error={true}>
            กรุณาระบุสินค้าที่มีในภาพให้ครบ
          </FormHelperText>
        ) : null}
      </Grid>
      <Grid item md={7}>
        <Stack
          sx={{
            pl: 3,
          }}
        >
          <label
            style={{
              alignSelf: "flex-start",
              fontSize: font.size.md,
              marginBlock: 12,
            }}
          >
            เลือกสินค้าที่มีในภาพ
          </label>
          <Autocomplete
            aria-required={true}
            multiple
            id="combo-box-similars"
            getOptionSelected={(option, value) => option._id === value._id}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={`item-${option._id}`} style={{}}>
                {!!option?.group_image?.product_display_image === false ? (
                  <ImageEmpty>
                    <InsertPhotoIcon
                      sx={{
                        color: "rgba(0, 0, 0, 0.54)",
                        cursor: "pointer",
                      }}
                    />
                  </ImageEmpty>
                ) : (
                  <img
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      objectPosition: "center",
                      borderRadius: "16px",
                      marginRight: "8px",
                    }}
                    src={
                      option?.group_image?.product_display_image
                        ? option?.group_image?.product_display_image.replace(
                            ".com",
                            ".com/300x300"
                          )
                        : ""
                    }
                    alt={option?.group_image?.product_display_image}
                  />
                )}
                <ProductChoiceDetail>
                  {/* name */}
                  <SpanProductDetail nameProd>
                    {option?.product?.item_name
                      ? option?.product?.item_name
                      : "ไม่มีชื่อ"}
                  </SpanProductDetail>

                  {/* size */}
                  <SpanProductDetail center>
                    {option?.size?.quantity === "size"
                      ? `${option?.size?.width} ${
                          getLocale() === "th"
                            ? option?.size?.unit_width?.unit_th
                            : option?.size?.unit_width?.unit_en
                        } x ${option?.size?.height} ${
                          getLocale() === "th"
                            ? option?.size?.unit_height?.unit_th
                            : option?.size?.unit_height?.unit_en
                        } x ${option?.size?.thickness} ${
                          getLocale() === "th"
                            ? option?.size?.unit_thickness?.unit_th
                            : option?.size?.unit_thickness?.unit_en
                        }`
                      : option?.size?.quantity === "capacity"
                      ? `${option?.size?.capacity_size} ${
                          getLocale() === "th"
                            ? option?.size?.unit_capacity?.unit_th
                            : option?.size?.unit_capacity?.unit_en
                        }`
                      : getLocale() === "th"
                      ? "สินค้าสั่งทำพิเศษ"
                      : "Made by order"}
                  </SpanProductDetail>

                  {/* item_code */}
                  <SpanProductDetail>
                    {option?.product?.item_code
                      ? option?.product?.item_code
                      : "-"}
                  </SpanProductDetail>

                  {/* choice name */}
                  <SpanProductDetail center>
                    {option?.name !== "" ? `( ${option?.name} )` : `-`}
                  </SpanProductDetail>

                  {/* price */}
                  <SpanProductDetail price>
                    {convertPrice(
                      option?.group_price?.is_start_price === true
                        ? option?.group_price?.start_price
                        : option?.group_price.special_price > 0 &&
                          option?.group_price.special_price !== null &&
                          option?.group_price.special_price !== undefined
                        ? option?.group_price.special_price
                        : option?.group_price?.price
                    )}
                    {/* {getLocale() === "th"
                            ? ` บาท / ` +
                              option?.group_price
                                ?.unit_price?.unit_th
                            : ` baht / ` +
                              option?.group_price
                                ?.unit_price?.unit_en} */}
                  </SpanProductDetail>
                </ProductChoiceDetail>
              </li>
            )}
            options={productList}
            limitTags={2}
            value={formData?.product_choice_moods}
            onChange={(event, newValue) => {
              if (
                formData?.product_choice_moods?.length > 1 &&
                newValue.length === 0
              ) {
                openDialog({
                  type: "confirm",
                  title: "คุณต้องการลบสินค้าทั้งหมดใช่หรือไม่?",
                  onConfirm: () => {
                    onChange(null, "product_choice_moods", newValue);
                    closeDialog();
                  },
                  maxWidth: 342,
                });
              } else {
                onChange(null, "product_choice_moods", newValue);
              }
            }}
            getOptionLabel={(option) => {
              return (
                option?.product?.item_code +
                " - " +
                option?.product?.item_name +
                (option.name !== "" ? ` - (` + option.name + `)` : ``)
              );
            }}
            filterOptions={createFilterOptions({
              matchFrom: "any",
              limit: 100,
            })}
            sx={{
              // mt: 2,
              // mb: 3,
              width: "100%",
              background: "#fff",
              borderRadius: "16px",
            }}
            renderInput={(params) => (
              <TextField
                className="TextField-without-border-radius"
                {...params}
                label="เลือกสินค้า"
                error={!!formErrors[`product_choice_moods`]}
                helperText={
                  !!formErrors[`product_choice_moods`]
                    ? "กรุณาเลือกสินค้า"
                    : "เลือกได้ไม่เกิน 5 สินค้า"
                }
              />
            )}
          />
          <label
            style={{
              alignSelf: "flex-start",
              fontSize: font.size.md,
              marginBlock: 12,
            }}
          >
            แฮชแท็ก
          </label>
          <Autocomplete
            id="combo-box-hashtag"
            fullWidth
            multiple
            options={sortHashtag}
            getOptionLabel={(option) => {
              // if (option.name.includes("#")) {
              //   return option.name;
              // }
              return `#${option.name}`;
            }}
            value={formData?.hashtags}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            onChange={(e, value) => {
              if (formData?.hashtags.length > 1 && value.length === 0) {
                openDialog({
                  type: "confirm",
                  title: "คุณต้องการลบแฮชแท็กทั้งหมดใช่หรือไม่?",
                  onConfirm: () => {
                    onChange(e, `hashtags`, value);
                    closeDialog();
                  },
                  maxWidth: 342,
                });
              } else {
                onChange(e, `hashtags`, value);
              }
            }}
            freeSolo
            disableCloseOnSelect
            ListboxComponent={forwardRef(
              ({ children, index, ...props }, ref) => (
                <ul
                  {...props}
                  ref={ref}
                  key={`list-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  {children}
                </ul>
              )
            )}
            renderOption={(props, option, { selected }, index) => (
              <>
                <li
                  {...props}
                  key={`hashtag-option-${index}`}
                  style={{
                    flex: "0 0 33%",
                    maxWidth: "33%",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      sx={{
                        "&.Mui-checked": {
                          color: "#E13B30",
                        },
                      }}
                      checked={selected}
                    />
                    {option.name}
                  </div>
                  <p
                    style={{
                      width: "100%",
                      margin: 0,
                      fontSize: "0.75rem",
                      color: "#6C6C70",
                      paddingLeft: "40px",
                    }}
                  >
                    ถูกใช้ไปแล้ว {option?.mood_count ?? 0} ครั้ง
                  </p>
                </li>
              </>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="#Hashtag"
                error={!!formErrors[`hashtags`]}
                helperText={
                  !!formErrors[`hashtags`]
                    ? "กรุณาเลือกแฮชแท็ก"
                    : "เลือกได้ไม่เกิน 5 แฮชแท็ก"
                }
              />
            )}
            style={{
              width: "100%",
              borderRadius: "16px",
              // marginBottom: "20px",

              ".MuiInputBase-root-MuiOutlinedInput-root-MuiSelect-root": {
                borderRadius: "16px",
              },
            }}
            filterOptions={createFilterOptions({
              matchFrom: "any",
              limit: 100,
            })}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
export default Step2;
